import React, { useState, useEffect, useRef } from 'react';

interface EmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (email: string) => void;
    tenantSlug: string;
    loading?: boolean;
}

const EmailModal: React.FC<EmailModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    tenantSlug,
    loading = false,
}) => {
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    // Reset email when modal opens
    useEffect(() => {
        if (isOpen) {
            setEmail('');
            setEmailError('');
            // Focus input after a small delay
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    // Handle Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) {
            setEmailError('Email is required');
            return false;
        }
        if (!emailRegex.test(email)) {
            setEmailError('Please enter a valid email address');
            return false;
        }
        setEmailError('');
        return true;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validateEmail(email)) {
            onSubmit(email);
        }
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEmail(value);
        // Clear error while typing
        if (emailError) {
            setEmailError('');
        }
    };

    if (!isOpen) return null;

    return (
        <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50'>
            <div
                className='bg-white rounded-lg shadow-lg max-w-md w-full'
                style={{
                    padding: '24px',
                }}
            >
                <h3
                    className='text-lg font-semibold mb-2'
                    style={{
                        color: '#3F5265',
                        fontSize: '18px',
                    }}
                >
                    Reset Password
                </h3>
                <p
                    className='text-sm mb-6'
                    style={{
                        color: '#657584',
                        fontSize: '14px',
                    }}
                >
                    Enter your email address and we'll send you a One Time Password (OTP) to reset your password.
                </p>

                <form onSubmit={handleSubmit}>
                    <div className='mb-6'>
                        <label
                            className='block text-sm font-medium mb-1'
                            style={{ color: '#9FA9B2', fontSize: '12px' }}
                        >
                            EMAIL ADDRESS<span className='text-red-500'>*</span>
                        </label>
                        <input
                            ref={inputRef}
                            type='email'
                            value={email}
                            onChange={handleEmailChange}
                            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${emailError ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder='you@example.com'
                            disabled={loading}
                            autoComplete='email'
                        />
                        {emailError && (
                            <p className='text-red-500 text-xs mt-1'>{emailError}</p>
                        )}
                        <p className='text-xs text-gray-500 mt-1'>
                            Tenant: <span className='font-medium'>{tenantSlug}</span>
                        </p>
                    </div>

                    <div className='flex gap-3 justify-end'>
                        <button
                            type='button'
                            onClick={onClose}
                            className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors'
                            disabled={loading}
                            style={{
                                fontSize: '14px',
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            disabled={loading || !email.trim()}
                            className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${loading || !email.trim()
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-orange-500 hover:bg-orange-600'
                                }`}
                            style={{
                                fontSize: '14px',
                            }}
                        >
                            {loading ? 'Sending...' : 'Send OTP'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EmailModal;